import styled from 'styled-components';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Typography, Button, Link } from '../../design-system';
import { scrollToAnimation } from '../../utils';
import { LANDING } from '../../const';
import HeroImgSrc from '../../images/Hero.png';
import AutomationImgSrc from '../../images/Automation.png';
import WhyChooseImgSrc from '../../images/WhyChoose.png';
import LOGO_PRIMARY_SVG from '../../images/LogoPrimary.svg';

// NAVIGATION

const NavigationBase = styled.nav`
  position: sticky;
  top: 0;

  height: 8.8rem;
  background-color: var(--white);
  z-index: 1000;
`;

const NavigationWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr;
  grid-column-gap: var(--spacing-6);
  align-items: center;

  max-width: 144rem;
  height: 100%;
  margin: auto;
  padding: 0 var(--spacing-20);
`;

const LogoBase = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  grid-column-gap: var(--spacing-2);
  align-items: center;
  height: 3.4rem;
`;

const LogoImg = styled.img`
  height: 3.4rem;
  object-fit: contain;
  object-position: center;
`;

const Logo = () => {
  return (
    <LogoBase>
      <LogoImg src={LOGO_PRIMARY_SVG} />
      <Typography variant={'h6'} weight={'bold'} color={'--primary-100'}>
        TEZPORT
      </Typography>
    </LogoBase>
  );
};

interface CustomLinkProps {
  to: string;
  text: string;
  onClick?: (e: any) => void;
}

const CustomLink: React.FC<CustomLinkProps> = ({ to, text, ...props }: CustomLinkProps) => {
  return (
    <Link to={to} maxwidth withunderline {...props}>
      <Typography variant="textM">{text}</Typography>
    </Link>
  );
};

export const Navigation = () => {
  return (
    <NavigationBase>
      <NavigationWrapper>
        <Logo />
        <CustomLink
          to={'#hero'}
          text={LANDING.NAV.HERO}
          onClick={(e) => scrollToAnimation(e, 'hero')}
        />
        <CustomLink
          to={'#key-features'}
          text={LANDING.NAV.KEY_FEATURES}
          onClick={(e) => scrollToAnimation(e, 'key-features')}
        />
        <CustomLink
          to={'#why-choose'}
          text={LANDING.NAV.WHY_CHOOSE}
          onClick={(e) => scrollToAnimation(e, 'why-choose')}
        />
        <CustomLink
          to={'#implementing'}
          text={LANDING.NAV.IMPLEMENTING}
          onClick={(e) => scrollToAnimation(e, 'implementing')}
        />
        <CustomLink
          to={'#reviews'}
          text={LANDING.NAV.REVIEWS}
          onClick={(e) => scrollToAnimation(e, 'reviews')}
        />
        <CustomLink
          to={'#contact-us'}
          text={LANDING.NAV.CONTACT_US}
          onClick={(e) => scrollToAnimation(e, 'contact-us')}
        />
        <Button to={'/signin'} size={'large'}>
          Sign In
        </Button>
      </NavigationWrapper>
    </NavigationBase>
  );
};

// LANDING_WRAPPER

export const LandingBase = styled.div`
  max-width: 144rem;
  margin: auto;
`;

// HERO

const HeroBase = styled.section`
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-column-gap: var(--spacing-5);

  padding: 0 var(--spacing-5);
  height: 67.5rem;
`;

const HeroBody = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  align-content: center;

  background-color: var(--gray-40);
  padding: 0 var(--spacing-16);

  & > *:nth-child(2) {
    margin-top: var(--spacing-6);
  }
`;

const HeroBodyButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  grid-column-gap: var(--spacing-4);

  margin-top: var(--spacing-16);
`;

const HeroImg = styled.img`
  width: 58.2rem;
  object-fit: cover;
  object-position: center;
`;

export const Hero = () => {
  return (
    <HeroBase id={'hero'}>
      <HeroBody>
        <Typography variant="h2" weight="semibold">
          {LANDING.HERO.TITLE}
        </Typography>
        <Typography variant="textL" weight="semibold">
          {LANDING.HERO.BODY_HEADER}
        </Typography>
        <Typography variant="textL">{LANDING.HERO.BODY}</Typography>
        <HeroBodyButtons>
          <Button size={'large'}>{LANDING.NAV.REQUEST_DEMO}</Button>
          <Button size={'large'} data-color="white">
            {LANDING.NAV.SUBMIT_REQUEST}
          </Button>
        </HeroBodyButtons>
      </HeroBody>
      <HeroImg src={HeroImgSrc} />
    </HeroBase>
  );
};

// SECTION_LIST

const SectionListBase = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(2, max-content);
  grid-column-gap: var(--spacing-20);

  padding: var(--spacing-20);
`;

const SectionListBody = styled.div`
  & > *:nth-child(n + 2) {
    margin-top: var(--spacing-6);
  }
`;

const SectionListItems = styled.div`
  & > * {
    display: list-item;
  }

  & > *:nth-child(n + 2) {
    margin-top: var(--spacing-6);
  }
`;

const SectionListImg = styled.img`
  width: 100%;
  min-height: 47rem;
  max-height: 56rem;

  object-fit: cover;
  object-position: center;
`;

interface SectionListProps {
  id?: string;
  title: string;
  body?: string;
  items: string[];
  img: any;
}

const SectionList: React.FC<SectionListProps> = ({
  title,
  body,
  items,
  img,
  ...props
}: SectionListProps) => {
  return (
    <SectionListBase {...props}>
      <SectionListBody>
        <Typography variant="h3" weight="semibold">
          {title}
        </Typography>
        {body && <Typography variant="textL">{body}</Typography>}
        <SectionListItems>
          {items.map((item, i) => (
            <Typography key={i} variant="textL">
              {item}
            </Typography>
          ))}
        </SectionListItems>
      </SectionListBody>
      <SectionListImg src={img} />
    </SectionListBase>
  );
};

// AUTOMATION

export const Automation = () => {
  return (
    <SectionList
      title={LANDING.AUTOMATION.TITLE}
      body={LANDING.AUTOMATION.BODY}
      items={LANDING.AUTOMATION.ITEMS}
      img={AutomationImgSrc}
    />
  );
};

// KEY_FEATURES

const KeyFeaturesBase = styled.section`
  margin: var(--spacing-5);
  padding: var(--spacing-20) var(--spacing-16);
  background-color: var(--gray-40);
`;

const KeyFeaturesItems = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 30.5rem;
  grid-gap: var(--spacing-5);

  margin-top: var(--font-size-9);
`;

const KeyFeaturesItemBase = styled.div`
  display: grid;
  grid-template-rows: 1fr repeat(2, max-content);
  grid-row-gap: var(--spacing-4);

  background-color: var(--white);
  padding: var(--spacing-5);

  & > .icon {
    width: 5.4rem;
    height: 5.4rem;
    padding: var(--spacing-3);

    fill: var(--white);
    background-color: var(--primary-100);
    border-bottom: solid 0.2rem var(--gray-100);
  }
`;

interface KeyFeaturesItemProps {
  Icon: any;
  head: string;
  body: string;
}

const KeyFeaturesItem: React.FC<KeyFeaturesItemProps> = ({
  Icon,
  head,
  body,
}: KeyFeaturesItemProps) => {
  return (
    <KeyFeaturesItemBase>
      <Icon className="icon" />
      <Typography variant="textL" weight="semibold">
        {head}
      </Typography>
      <Typography variant="textM">{body}</Typography>
    </KeyFeaturesItemBase>
  );
};

export const KeyFeatures = () => {
  return (
    <KeyFeaturesBase id="key-features">
      <Typography variant="h3" weight="semibold">
        {LANDING.KEY_FEATURES.TITLE}
      </Typography>
      <KeyFeaturesItems>
        {LANDING.KEY_FEATURES.ITEMS.map(({ ICON, HEAD, BODY }, i) => (
          <KeyFeaturesItem key={i} Icon={ICON} head={HEAD} body={BODY} />
        ))}
      </KeyFeaturesItems>
    </KeyFeaturesBase>
  );
};

// WHY_CHOOSE

export const WhyChoose = () => {
  return (
    <SectionList
      id={'why-choose'}
      title={LANDING.WHY_CHOOSE.TITLE}
      items={LANDING.WHY_CHOOSE.ITEMS}
      img={WhyChooseImgSrc}
    />
  );
};

// IMPLEMENTING

const ImplementingBase = styled.section`
  background-color: var(--primary-100);
  padding: var(--spacing-20);
`;

const ImplementingCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: var(--spacing-5);

  margin-top: var(--spacing-6);
`;

const ImplementingCardBase = styled.div`
  background-color: var(--white);
`;

const ImplementingCardImg = styled.img`
  object-fit: cover;
  object-position: center;
`;

const ImplementingCardBody = styled.div`
  padding: var(--spacing-4);

  & > *:nth-child(n + 2) {
    margin-top: var(--spacing-3);
  }
`;

interface ImplementingCardProps {
  img: any;
  title: string;
  items: string[];
}

const ImplementingCard: React.FC<ImplementingCardProps> = ({
  img,
  title,
  items,
}: ImplementingCardProps) => {
  return (
    <ImplementingCardBase>
      <ImplementingCardImg src={img} />
      <ImplementingCardBody>
        <Typography variant="h4" weight="medium">
          {title}
        </Typography>
        {items.map((item, i) => (
          <Typography variant="h6" key={i}>
            {item}
          </Typography>
        ))}
      </ImplementingCardBody>
    </ImplementingCardBase>
  );
};

export const Implementing = () => {
  return (
    <ImplementingBase id={'implementing'}>
      <Typography variant="h3" weight="semibold" color="--white">
        {LANDING.IMPLEMENTING.TITLE}
      </Typography>
      <ImplementingCards>
        {LANDING.IMPLEMENTING.CARDS.map(({ IMG, TITLE, ITEMS }, i) => (
          <ImplementingCard key={i} img={IMG} title={TITLE} items={ITEMS} />
        ))}
      </ImplementingCards>
    </ImplementingBase>
  );
};

// REVIEWS

const ReviewsBase = styled.section`
  display: grid;
  grid-row-gap: var(--spacing-6);

  padding: var(--spacing-20);
  background-color: var(--gray-40);
`;

const ReviewsItems = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: var(--spacing-6);
`;

const ReviewItemBase = styled.div`
  background-color: var(--white);
  padding: var(--spacing-6);

  & > *:nth-child(2) {
    margin-top: var(--spacing-8);
  }
`;

const ReviewItemHeader = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  grid-template-rows: max-content 1fr;
  grid-column-gap: var(--spacing-4);
  grid-row-gap: 0.2rem;
`;

const ReviewItemImg = styled.img`
  width: 7.6rem;
  height: 7.6rem;

  grid-row: 1 / 3;
`;

interface ReviewItemProps {
  img: any;
  name: string;
  job: string;
  date: string;
  review: string;
}

const ReviewItem: React.FC<ReviewItemProps> = ({
  img,
  name,
  job,
  date,
  review,
}: ReviewItemProps) => {
  return (
    <ReviewItemBase>
      <ReviewItemHeader>
        <ReviewItemImg src={img} />
        <Typography variant="textL" weight="medium">
          {name}
        </Typography>
        <Typography variant="textM" color="--gray-70">
          {date}
        </Typography>
        <Typography variant="textM" color="--gray-70">
          {job}
        </Typography>
      </ReviewItemHeader>
      <Typography variant="textL" color="--gray-90">
        {review}
      </Typography>
    </ReviewItemBase>
  );
};

export const Reviews = () => {
  return (
    <ReviewsBase id={'reviews'}>
      <Typography variant="h3" weight="semibold">
        {LANDING.REVIEWS.TITLE}
      </Typography>
      <ReviewsItems>
        {LANDING.REVIEWS.ITEMS.map(({ IMG, NAME, JOB, DATE, REVIEW }, i) => (
          <ReviewItem key={i} img={IMG} name={NAME} job={JOB} date={DATE} review={REVIEW} />
        ))}
      </ReviewsItems>
    </ReviewsBase>
  );
};

// FAQ

const FAQBase = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr max-content;
  grid-gap: var(--spacing-20);

  padding: var(--spacing-20);
`;

const ContactUsBody = styled.div`
  & > *:nth-child(n + 2) {
    margin-top: var(--spacing-4);
  }
`;

const ContactUsChats = styled.div`
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  grid-column-gap: var(--spacing-4);
`;

const FAQItems = styled.div`
  grid-column: 2;
  grid-row: 1 / 3;
`;

const FAQItemHeader = styled(AccordionSummary)`
  height: 9.2rem;
  padding: 0 var(--spacing-4);
  background-color: var(--white);

  & svg {
    width: 2.4rem;
    height: 2.4rem;
    fill: var(--gray-100);
  }
`;

export const FAQ = () => {
  return (
    <FAQBase>
      <Typography variant="h3" weight="semibold">
        {LANDING.FAQ.TITLE}
      </Typography>
      <ContactUsBody id={'contact-us'}>
        <Typography variant="textL" weight="medium">
          {LANDING.CONTACT_US.TITLE}
        </Typography>
        <CustomLink to={'#'} text={LANDING.CONTACT_US.ITEM_PHONE} />
        <CustomLink to={'#'} text={LANDING.CONTACT_US.ITEM_EMAIL} />
        <ContactUsChats>
          {LANDING.CONTACT_US.ITEM_CHATS.map((chat, i) => (
            <CustomLink key={i} to={'#'} text={chat} />
          ))}
        </ContactUsChats>
      </ContactUsBody>
      <FAQItems>
        {LANDING.FAQ.ITEMS.map(({ QUESTION, ANSWER }, i) => (
          <Accordion key={i}>
            <FAQItemHeader expandIcon={<ExpandMoreIcon />}>
              <Typography variant="textL" weight="medium">
                {QUESTION}
              </Typography>
            </FAQItemHeader>
            <AccordionDetails>
              <Typography variant="textM" color="--gray-70">
                {ANSWER}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </FAQItems>
    </FAQBase>
  );
};

// FINAL_CTA

const FinalCTABase = styled.section`
  display: grid;
  grid-row-gap: var(--spacing-6);
  justify-items: center;

  margin: var(--spacing-5);
  padding: var(--spacing-24);
  background-color: var(--primary-100);

  & > *:last-child {
    margin-top: var(--spacing-6);
  }
`;

export const FinalCTA = () => {
  return (
    <FinalCTABase>
      <Typography variant="h3" weight="semibold" color="--white">
        {LANDING.FINAL_CTA.TITLE}
      </Typography>
      <Typography variant="textM" color="--white">
        {LANDING.FINAL_CTA.SUB_TITLE}
      </Typography>
      <Button size={'large'} data-color="white">
        {LANDING.NAV.REQUEST_DEMO}
      </Button>
    </FinalCTABase>
  );
};
